import { canAccessPath } from '../utils/access';
import { Link, useLocation } from 'react-router-dom';
import {
  Building2, Users, LogOut, ChevronDown, LayoutDashboard, ShieldAlert, Bell,
  Radar, ScrollText, ShieldOff, ListChecks, Settings, Wrench, Server, Boxes, Activity, Radio,
  FlaskConical, UserCog, FileText, LayoutTemplate, ShieldCheck, Siren, PanelLeftClose, PanelLeftOpen, BookOpen, ScreenShare, Grid3x3, ClipboardList, FileSignature,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTenantScope } from '../context/TenantScopeContext';
import SocPlatformLogo from './SocPlatformLogo';
import ExportMenu from './ExportMenu';
import NotificationBell from './NotificationBell';
import HelpMenu from './HelpMenu';
import GlobalSearch from './GlobalSearch';

const MANAGEMENT_NAV = [
  { to: '/partners', label: 'Partners', icon: Building2, minScope: 'platform' as const },
  { to: '/tenants', label: 'Tenants', icon: Users, minScope: 'partner' as const },
];

// minScope: la voce compare solo a chi opera almeno a quel livello. Va messo sulle pagine le cui
// API sono riservate (Parser Lab e Platform Settings rispondono 403 a chi non è platform):
// senza, l'utente vedeva una voce che portava a una pagina vuota con un errore.
const OPERATIONS_NAV: { label: string; items: { to: string; label: string; icon: LucideIcon; minScope?: 'platform' | 'partner' }[] }[] = [
  { label: 'Home', items: [
    { to: '/soc-ops', label: 'SOC Operations', icon: Siren },
    { to: '/overview', label: 'Overview', icon: LayoutDashboard },
    { to: '/data-quality', label: 'Data Quality', icon: Activity },
  ] },
  {
    label: 'Detections',
    items: [
      { to: '/incidents', label: 'Security Incidents', icon: ShieldAlert },
      { to: '/alerts', label: 'Security Alerts', icon: Bell },
      { to: '/risky-assets', label: 'Risky Assets', icon: Radar },
      { to: '/logs', label: 'Security Logs', icon: ScrollText },
      { to: '/whitelists', label: 'Whitelists', icon: ShieldOff },
      { to: '/detection-rules', label: 'Custom Detection Rules', icon: ListChecks },
      { to: '/mitre-coverage', label: 'MITRE Coverage', icon: Grid3x3 },
    ],
  },
  { label: 'Responses', items: [{ to: '/responses', label: 'SOAR', icon: Wrench }] },
  { label: 'Risk Management', items: [{ to: '/risk-management', label: 'Vulnerabilities', icon: ShieldOff }] },
  { label: 'Asset Center', items: [{ to: '/asset-center', label: 'Asset Inventory', icon: Boxes }, { to: '/remote-access', label: 'Remote Access Tools', icon: ScreenShare }] },
  { label: 'Reports', items: [
    { to: '/reports', label: 'Reports', icon: FileText },
    { to: '/report-templates', label: 'Report Templates', icon: LayoutTemplate },
  ] },
  { label: 'Configuration', items: [{ to: '/onboarding', label: 'Tenant Onboarding', icon: ClipboardList, minScope: 'partner' }, { to: '/onboarding-template', label: 'Onboarding Document', icon: FileSignature, minScope: 'partner' }, { to: '/sites-brokers', label: 'Sites & Brokers', icon: Server }, { to: '/log-sources', label: 'Log Sources', icon: Radio }, { to: '/parser-lab', label: 'Parser Lab', icon: FlaskConical, minScope: 'platform' }, { to: '/users', label: 'Users', icon: UserCog }, { to: '/settings', label: 'Platform Settings', icon: Settings, minScope: 'platform' }, { to: '/guides', label: 'Guides', icon: BookOpen }] },
];

// Pagine a tema scuro (vedi theme/console.ts).
const CONSOLE_ROUTES = ['/soc-ops', '/overview'];

const SCOPE_RANK: Record<string, number> = { platform: 3, partner: 2, tenant: 1 };
const SCOPE_LABELS: Record<string, string> = { platform: 'Platform', partner: 'Partner', tenant: 'Tenant' };

// Iniziali di nome e cognome (prima e ultima parola del nome); senza nome, la prima lettera della mail.
function initials(name?: string, email?: string): string {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return (email?.charAt(0) || '?').toUpperCase();
  return (words[0].charAt(0) + (words.length > 1 ? words[words.length - 1].charAt(0) : '')).toUpperCase();
}

interface Stats {
  logsCount: number;
  alertsCount: number;
  casesCount: number;
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  // Persistita come le preferenze di colonna delle griglie: chi lavora su schermi stretti si
  // aspetta di ritrovare la barra come l'ha lasciata, non di richiuderla ad ogni pagina.
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('soc.sidebarCollapsed') === '1');
  const toggleSidebar = () => setCollapsed(prev => {
    const next = !prev;
    localStorage.setItem('soc.sidebarCollapsed', next ? '1' : '0');
    return next;
  });
  const [stats, setStats] = useState<Stats | null>(null);
  const { tenants, selectedTenantId, setSelectedTenantId } = useTenantScope();

  // Voci di menu visibili all'utente, riusate dalla ricerca globale come "pagine".
  const visibleManagement = user && user.scopeLevel !== 'tenant'
    ? MANAGEMENT_NAV.filter(item => SCOPE_RANK[user.scopeLevel] >= SCOPE_RANK[item.minScope]) : [];
  const visibleGroups = OPERATIONS_NAV.map(group => ({
    ...group,
    items: group.items.filter(item => (!item.minScope || SCOPE_RANK[user?.scopeLevel || 'tenant'] >= SCOPE_RANK[item.minScope]) && canAccessPath(user, item.to)),
  })).filter(group => group.items.length > 0);

  useEffect(() => {
    axios.get('/api/stats', { params: { tenantId: selectedTenantId ?? undefined } }).then(res => setStats(res.data.data));
  }, [selectedTenantId]);

  return (
    <div className="min-h-screen bg-gray-50">
      <aside className={`fixed left-0 top-0 h-full ${collapsed ? 'w-16' : 'w-64'} bg-gray-900 text-white flex flex-col z-30 overflow-y-auto overflow-x-hidden transition-[width] duration-200`}>
        <div className={`flex items-center border-b border-gray-700 ${collapsed ? 'justify-center py-3' : 'justify-between px-4 py-4'}`}>
          {!collapsed && <img src="/logo_omega_trasp.png" alt="OmegaLab Logo" className="h-14 w-auto object-contain" />}
          <button onClick={toggleSidebar} title={collapsed ? 'Espandi il menu' : 'Comprimi il menu'}
            className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-800">
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>

        <nav className="flex-1 px-3 py-4">
          {user && user.scopeLevel !== 'tenant' && (
            <>
              {!collapsed && <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 pb-1">Management</p>}
              <ul className="space-y-1 mb-3">
                {visibleManagement.map(item => {
                  const Icon = item.icon;
                  const active = location.pathname.startsWith(item.to);
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        title={collapsed ? item.label : undefined}
                        className={`flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                          collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'
                        } ${active ? 'bg-primary-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
                      >
                        <Icon size={16} className="shrink-0" />
                        {!collapsed && item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          <div className={`px-3 pb-3 ${collapsed ? 'hidden' : ''}`}>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Viewing</label>
            <select
              value={selectedTenantId ?? ''}
              disabled={user?.scopeLevel === 'tenant'}
              onChange={e => setSelectedTenantId(e.target.value ? Number(e.target.value) : null)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-white disabled:opacity-70"
            >
              {user?.scopeLevel !== 'tenant' && <option value="">All tenants</option>}
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          {visibleGroups.map(group => (
            <div key={group.label} className="mb-3">
              {!collapsed && <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 pb-1">{group.label}</p>}
              <ul className="space-y-1">
                {group.items.map(item => {
                  const Icon = item.icon;
                  // Anche le sotto-rotte (/guides/:slug, /alerts/:id) tengono evidenziata la voce.
                  const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        title={collapsed ? item.label : undefined}
                        className={`flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-150 ${
                          collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'
                        } ${active ? 'bg-primary-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
                      >
                        <Icon size={16} className="shrink-0" />
                        {!collapsed && item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      <header className={`fixed top-0 ${collapsed ? 'left-16' : 'left-64'} right-0 h-16 bg-gray-900 border-b border-gray-800 flex items-center justify-between px-6 z-20 transition-[left] duration-200`}>
        <div className="flex items-center gap-6 text-sm text-gray-400">
          {stats && (
            <>
              <span>Logs <span className="text-sky-400 font-semibold">{stats.logsCount}</span></span>
              <span>Alerts <span className="text-sky-400 font-semibold">{stats.alertsCount}</span></span>
              <span>Incidents <span className="text-sky-400 font-semibold">{stats.casesCount}</span></span>
            </>
          )}
        </div>

        {/* Centrato rispetto alla finestra, non rispetto allo spazio fra i due blocchi laterali:
            così non si sposta quando i contatori cambiano numero di cifre o la sidebar si comprime. */}
        <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 hidden lg:flex items-center">
          <SocPlatformLogo />
        </div>
        <div className="flex items-center gap-2">
        <GlobalSearch pages={[...visibleManagement, ...visibleGroups.flatMap(g => g.items)]} />
        <HelpMenu />
        <NotificationBell />
        <ExportMenu />
        <div className="relative">
          {/* Nome e cognome al posto di "Platform · admin", iniziali nel cerchio (26/10/2026);
              livello e ruolo restano nel tooltip e nella tendina. */}
          <button
            onClick={() => setShowDropdown(v => !v)}
            title={`${SCOPE_LABELS[user?.scopeLevel || '']} · ${user?.role}`}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white text-xs font-semibold tracking-wide">
              {initials(user?.name, user?.email)}
            </div>
            <p className="hidden md:block text-sm font-medium text-gray-100 max-w-[12rem] truncate">{user?.name || user?.email}</p>
            <ChevronDown size={16} className="text-gray-400" />
          </button>
          {showDropdown && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
              <div className="absolute right-0 top-full mt-1 w-48 card z-20">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{SCOPE_LABELS[user?.scopeLevel || '']} · {user?.role}</p>
                </div>
                <Link
                  to="/security"
                  onClick={() => setShowDropdown(false)}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <ShieldCheck size={15} /> Security
                </Link>
                <button
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-b-xl"
                  onClick={logout}
                >
                  <LogOut size={15} /> Log out
                </button>
              </div>
            </>
          )}
        </div>
        </div>
      </header>

      <main className={`${collapsed ? 'ml-16' : 'ml-64'} pt-16 transition-[margin] duration-200 ${
        // Le pagine di monitoraggio continuo (SOC Operations, Overview) sono a tema scuro e
        // dipingono il proprio fondo a tutta area: qui si toglie padding e sfondo chiaro, che
        // altrimenti lascerebbero una cornice grigia attorno alla console.
        CONSOLE_ROUTES.includes(location.pathname) ? 'bg-[#0b1220] min-h-[calc(100vh-4rem)] p-6' : 'p-6'
      }`}>{children}</main>
    </div>
  );
}
